import html2canvas from 'html2canvas';

// Template interface
export interface AdTemplate {
  id: string;
  name: string;
  elements: {
    logo: ElementPosition;
    businessName: ElementPosition;
    headline: ElementPosition;
    copy: ElementPosition;
    cta: ElementPosition;
    hashtags: ElementPosition;
  };
  styles: {
    businessNameFont: string;
    businessNameFontSize: string;
    businessNameColor: string;
    headlineFont: string;
    headlineFontSize: string;
    headlineColor: string;
    copyFont: string;
    copyFontSize: string;
    copyColor: string;
    ctaFont: string;
    ctaFontSize: string;
    ctaBackgroundColor: string;
    ctaTextColor: string;
    hashtagsFont: string;
    hashtagsFontSize: string;
    hashtagsColor: string;
  };
  zIndexOrder: string[];
  createdAt: string;
}

export interface ElementPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
}

// Template Storage (LocalStorage)
export class TemplateManager {
  private static STORAGE_KEY = 'ad_templates';

  static saveTemplate(template: AdTemplate): void {
    const templates = this.getAllTemplates();
    const existingIndex = templates.findIndex(t => t.id === template.id);
    
    if (existingIndex >= 0) {
      templates[existingIndex] = template;
    } else {
      templates.push(template);
    }
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(templates));
  }

  static getAllTemplates(): AdTemplate[] {
    const data = localStorage.getItem(this.STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }

  static getTemplate(id: string): AdTemplate | null {
    const templates = this.getAllTemplates();
    return templates.find(t => t.id === id) || null;
  }

  static deleteTemplate(id: string): void {
    const templates = this.getAllTemplates();
    const filtered = templates.filter(t => t.id !== id);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
  }

  static generateTemplateId(): string {
    return `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Canvas Export Utilities
export class CanvasExporter {
  /**
   * Export canvas element to PNG image
   */
  static async exportToPNG(
    canvasElement: HTMLElement,
    filename: string,
    width: number,
    height: number
  ): Promise<void> {
    try {
      const canvas = await html2canvas(canvasElement, {
        width,
        height,
        scale: 2, // Higher quality
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error('Failed to export canvas');
    }
  }

  /**
   * Export multiple platform sizes at once
   */
  static async exportMultiplePlatforms(
    getCanvasElement: () => HTMLElement | null,
    platforms: Array<{ platform: string; size: string; width: number; height: number }>,
    baseFilename: string
  ): Promise<void> {
    for (const config of platforms) {
      const canvas = getCanvasElement();
      if (canvas) {
        const filename = `${baseFilename}_${config.platform}_${config.size}.png`;
        await this.exportToPNG(canvas, filename, config.width, config.height);
        
        // Small delay between exports
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  }

  /**
   * Get canvas as data URL for preview
   */
  static async getCanvasDataURL(
    canvasElement: HTMLElement,
    width: number,
    height: number
  ): Promise<string> {
    const canvas = await html2canvas(canvasElement, {
      width,
      height,
      scale: 1,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true
    });

    return canvas.toDataURL('image/png');
  }
}

// Z-Index Manager
export class ZIndexManager {
  static moveToFront(elementKey: string, currentOrder: string[]): string[] {
    const filtered = currentOrder.filter(key => key !== elementKey);
    return [...filtered, elementKey];
  }

  static moveToBack(elementKey: string, currentOrder: string[]): string[] {
    const filtered = currentOrder.filter(key => key !== elementKey);
    return [elementKey, ...filtered];
  }

  static moveUp(elementKey: string, currentOrder: string[]): string[] {
    const index = currentOrder.indexOf(elementKey);
    if (index < currentOrder.length - 1) {
      const newOrder = [...currentOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    }
    return currentOrder;
  }

  static moveDown(elementKey: string, currentOrder: string[]): string[] {
    const index = currentOrder.indexOf(elementKey);
    if (index > 0) {
      const newOrder = [...currentOrder];
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      return newOrder;
    }
    return currentOrder;
  }

  static getZIndex(elementKey: string, order: string[]): number {
    return order.indexOf(elementKey) + 10; // Base z-index of 10
  }
}

// Resize Handler for Moveable
export interface ResizeEvent {
  width: number;
  height: number;
  delta: [number, number];
}

export interface RotateEvent {
  rotate: number;
  delta: number;
}

export class ElementTransformUtils {
  static handleResize(
    elementKey: string,
    event: ResizeEvent,
    updateCallback: (key: string, updates: Partial<ElementPosition>) => void
  ): void {
    updateCallback(elementKey, {
      width: Math.max(20, event.width),
      height: Math.max(20, event.height)
    });
  }

  static handleRotate(
    elementKey: string,
    event: RotateEvent,
    updateCallback: (key: string, updates: Partial<ElementPosition>) => void
  ): void {
    updateCallback(elementKey, {
      rotation: event.rotate
    });
  }

  static constrainToCanvas(
    position: { x: number; y: number },
    element: { width: number; height: number },
    canvas: { width: number; height: number }
  ): { x: number; y: number } {
    return {
      x: Math.max(0, Math.min(position.x, canvas.width - element.width)),
      y: Math.max(0, Math.min(position.y, canvas.height - element.height))
    };
  }
}
